import { Sparkles, Copy, CheckCircle2, Clock } from 'lucide-react';
import { useState, useEffect, useRef, CSSProperties } from 'react';
import { Theme } from '@/shared/constants/themes';

interface Suggestion {
    id: number;
    text: string;
    type: string;
    score: number;
}

interface LiveSuggestionsProps {
    suggestions?: Suggestion[];
    timestamp?: string; // ISO timestamp of when suggestions were generated
    theme?: Theme;
}

export const LiveSuggestions: React.FC<LiveSuggestionsProps> = ({ suggestions = [], timestamp, theme }) => {
    const themeColors = theme?.colors;
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [isNew, setIsNew] = useState(false);
    const prevTimestampRef = useRef<string | undefined>(timestamp);

    // Detect when suggestions change
    useEffect(() => {
        if (timestamp && timestamp !== prevTimestampRef.current) {
            setIsNew(true);
            prevTimestampRef.current = timestamp;

            // Remove animation after 3 seconds
            const timer = setTimeout(() => setIsNew(false), 3000);
            return () => clearTimeout(timer);
        }
    }, [timestamp]);

    const copyToClipboard = (text: string, id: number) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const getTypeConfig = (type: string, themeColors?: any) => {
        switch (type) {
            case 'interaction': return {
                style: {
                    backgroundColor: themeColors?.success || '#10B981',
                    color: '#FFFFFF',
                    borderColor: themeColors?.success || '#10B981'
                },
                label: '💬 Interaction'
            };
            case 'style': return {
                style: {
                    backgroundColor: themeColors?.primary || '#9333EA',
                    color: '#FFFFFF',
                    borderColor: themeColors?.primary || '#9333EA'
                },
                label: '👗 Style'
            };
            case 'jp': return {
                style: {
                    backgroundColor: themeColors?.warning || '#F59E0B',
                    color: '#FFFFFF',
                    borderColor: themeColors?.warning || '#F59E0B'
                },
                label: '⚡ JP'
            };
            case 'jeu': return {
                style: {
                    backgroundColor: themeColors?.secondary || '#EC4899',
                    color: '#FFFFFF',
                    borderColor: themeColors?.secondary || '#EC4899'
                },
                label: '🎮 Jeu'
            };
            default: return {
                style: {
                    backgroundColor: themeColors?.accent || themeColors?.primary || '#9333EA',
                    color: '#FFFFFF',
                    borderColor: themeColors?.accent || themeColors?.primary || '#9333EA'
                },
                label: '✨ IA'
            };
        }
    };

    const formatTimestamp = (timestamp?: string | any) => {
        if (!timestamp) return '';

        // Handle Firestore Timestamp object
        if (timestamp?.toDate) {
            return timestamp.toDate().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
        }

        // Handle ISO string
        if (typeof timestamp === 'string') {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
                return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
            }
        }

        return '';
    };

    return (
        <div
            className={`rounded-lg shadow-sm border overflow-hidden transition-all duration-300 ${isNew ? 'ring-2 shadow-lg' : ''}`}
            style={{
                backgroundColor: themeColors?.surface || '#FFFFFF',
                borderColor: isNew ? themeColors?.primary || '#9333EA' : (themeColors?.border || '#E5E7EB'),
                '--tw-ring-color': themeColors?.primary || '#9333EA'
            } as CSSProperties & { '--tw-ring-color': string }}
        >
            {/* Header */}
            <div
                className={`px-4 py-3 border-b transition-all duration-300`}
                style={{
                    background: isNew
                        ? `linear-gradient(to right, ${themeColors?.primaryLight || '#A78BFA'}20, ${themeColors?.secondaryLight || '#F9A8D4'}20)`
                        : `linear-gradient(to right, ${themeColors?.primaryLight || '#A78BFA'}15, ${themeColors?.secondaryLight || '#F9A8D4'}15)`,
                    borderBottomColor: themeColors?.border || '#E5E7EB'
                }}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles
                            size={16}
                            className={isNew ? 'animate-pulse' : ''}
                            style={{ color: themeColors?.primary || '#9333EA' }}
                        />
                        <h3
                            className="text-sm font-semibold"
                            style={{ color: themeColors?.foreground || '#374151' }}
                        >
                            Suggestions IA
                        </h3>
                        {isNew && (
                            <span
                                className="text-xs font-bold px-2 py-0.5 rounded-full animate-bounce"
                                style={{
                                    color: themeColors?.primary || '#9333EA',
                                    backgroundColor: themeColors?.primaryLight || '#F3E8FF'
                                }}
                            >
                                NOUVEAU
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {timestamp && (
                            <div
                                className="flex items-center gap-1 text-xs"
                                style={{ color: themeColors?.foregroundSecondary || '#6B7280' }}
                            >
                                <Clock size={12} />
                                <span>{formatTimestamp(timestamp)}</span>
                            </div>
                        )}
                        <span
                            className="text-xs font-medium px-2 py-0.5 rounded-full"
                            style={{
                                color: themeColors?.primary || '#9333EA',
                                backgroundColor: themeColors?.surface || '#FFFFFF'
                            }}
                        >
                            {suggestions.length} phrases
                        </span>
                    </div>
                </div>
            </div>

            {/* Suggestions List */}
            <div className="p-4 space-y-2.5">
                {suggestions.length === 0 ? (
                    <p
                        className="text-xs italic text-center py-6"
                        style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                    >
                        En attente de suggestions IA...
                    </p>
                ) : (
                    suggestions.map((suggestion, index) => {
                        const typeConfig = getTypeConfig(suggestion.type, themeColors);
                        const isCopied = copiedId === suggestion.id;

                        return (
                            <div
                                key={suggestion.id}
                                className={`group rounded-lg p-3 border transition-all ${isNew ? 'animate-slideIn' : ''}`}
                                style={{
                                    animationDelay: `${index * 100}ms`,
                                    backgroundColor: themeColors?.backgroundSecondary || '#F9FAFB',
                                    borderColor: themeColors?.border || '#E5E7EB'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = themeColors?.primary || '#9333EA';
                                    e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = themeColors?.border || '#E5E7EB';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            >
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <span
                                        className="text-xs font-bold px-2 py-0.5 rounded-full border"
                                        style={typeConfig.style}
                                    >
                                        {typeConfig.label}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <div
                                            className="text-sm font-bold px-2 py-0.5 rounded-full mt-1"
                                            style={{
                                                color: typeConfig.style.color,
                                                backgroundColor: typeConfig.style.backgroundColor
                                            }}
                                        >
                                            {suggestion.score}%
                                        </div>
                                        <button
                                            onClick={() => copyToClipboard(suggestion.text, suggestion.id)}
                                            className="p-1.5 rounded-md transition-colors"
                                            style={{
                                                backgroundColor: 'transparent'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor = themeColors?.backgroundTertiary || '#F3F4F6';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor = 'transparent';
                                            }}
                                            title="Copier"
                                        >
                                            {isCopied ? (
                                                <CheckCircle2
                                                    size={14}
                                                    style={{ color: themeColors?.success || '#10B981' }}
                                                />
                                            ) : (
                                                <Copy
                                                    size={14}
                                                    style={{ color: themeColors?.foregroundSecondary || '#9CA3AF' }}
                                                />
                                            )}
                                        </button>
                                    </div>
                                </div>
                                <p
                                    className="text-sm leading-relaxed font-medium"
                                    style={{ color: themeColors?.foreground || '#111827' }}
                                >
                                    {suggestion.text}
                                </p>
                            </div>
                        );
                    })
                )}
            </div>

            <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        
        .animate-slideIn {
          animation: slideIn 0.4s ease-out forwards;
        }
      `}</style>
        </div>
    );
};
